import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, RotateCcw, X, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api, APIError } from '@/lib/api';

/* ------------------------------- Types ---------------------------------- */

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  /** When set on a system note, render a Retry button (used for 503s). */
  retry?: boolean;
}

interface ChatResponse {
  message: { role: 'assistant'; content: string };
  quotaRemaining?: number;
}

interface PersistedState {
  messages: ChatMessage[];
  quotaRemaining?: number;
}

/* ----------------------------- Constants --------------------------------- */

const STORAGE_KEY = 'telos-ai-chat-v1';

// Mirrors `WATCHLIST_KEY` in pages/panels/ResearchPanel.tsx — kept in sync by
// convention. If that key changes, update here too.
const WATCHLIST_KEY = 'telos:research:watchlist';

// Same easing tokens used across the app (PairDetailDrawer / SupportFeedbackModal).
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const EXAMPLE_PROMPTS: string[] = [
  'What should I watch on EUR/USD this week?',
  "Explain today's CPI print",
  'Why is NVDA moving?',
  'Best risk-on plays right now?',
];

/* ----------------------------- Helpers ----------------------------------- */

function loadPersisted(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { messages: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages)) return { messages: [] };
    const messages: ChatMessage[] = parsed.messages
      .filter((m: any): m is ChatMessage =>
        m && typeof m.content === 'string' &&
        (m.role === 'user' || m.role === 'assistant' || m.role === 'system'),
      )
      .map((m: any) => ({
        role: m.role,
        content: m.content,
        ...(m.retry ? { retry: true } : {}),
      }));
    const quotaRemaining =
      typeof parsed.quotaRemaining === 'number' ? parsed.quotaRemaining : undefined;
    return { messages, quotaRemaining };
  } catch {
    return { messages: [] };
  }
}

function savePersisted(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

function readWatchlist(): string[] {
  try {
    const raw = localStorage.getItem(WATCHLIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string');
  } catch {
    return [];
  }
}

/**
 * Bias / pulse pairs are stored server-side under `/api/preferences` as
 * `pulsePairs`. We fetch them lazily — a fire-and-forget GET that fails
 * gracefully if the endpoint hiccups. The widget always sends *something* (or
 * nothing) here, never blocking on this lookup.
 */
async function fetchBiasPairs(signal: AbortSignal): Promise<string[]> {
  try {
    const prefs = await api.get<{ pulsePairs?: string[] }>('/api/preferences', { signal });
    const arr = prefs?.pulsePairs;
    if (!Array.isArray(arr)) return [];
    return arr.filter((s): s is string => typeof s === 'string');
  } catch {
    return [];
  }
}

/** Strip the transient `retry` flag — only model-visible roles go to the API. */
function toApiMessages(messages: ChatMessage[]): { role: 'user' | 'assistant'; content: string }[] {
  return messages
    .filter((m): m is ChatMessage & { role: 'user' | 'assistant' } =>
      m.role === 'user' || m.role === 'assistant',
    )
    .map((m) => ({ role: m.role, content: m.content }));
}

/* ------------------------------- Widget ---------------------------------- */

export function AIChatWidget() {
  const initial = useMemo(loadPersisted, []);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(initial.messages);
  const [quotaRemaining, setQuotaRemaining] = useState<number | undefined>(
    initial.quotaRemaining,
  );
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Persist on every change.
  useEffect(() => {
    savePersisted({ messages, quotaRemaining });
  }, [messages, quotaRemaining]);

  // Esc to close while drawer is open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // External openers (e.g. the dashboard onboarding checklist) can request
  // the drawer be opened by dispatching a `telos:open-ai-chat` event on
  // window. Keeps the widget self-contained — no prop drilling.
  useEffect(() => {
    function onOpen() {
      setOpen(true);
    }
    window.addEventListener('telos:open-ai-chat', onOpen);
    return () => window.removeEventListener('telos:open-ai-chat', onOpen);
  }, []);

  /* -- send flow ---------------------------------------------------------- */

  const sendRef = useRef<AbortController | null>(null);

  // `baseHistoryOverride` lets retry skip the trailing system notes without
  // racing with React's state batching — we use the explicit value instead of
  // closed-over `messages` for building the request payload.
  const sendMessage = useCallback(
    async (text: string, baseHistoryOverride?: ChatMessage[]) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;

      const baseHistory = baseHistoryOverride ?? messages;

      // Optimistic append of the user's message.
      const userMsg: ChatMessage = { role: 'user', content: trimmed };
      const nextMessages = [...baseHistory, userMsg];
      setMessages(nextMessages);
      setInput('');
      setSending(true);

      // Cancel any prior in-flight request defensively.
      if (sendRef.current) sendRef.current.abort();
      const ctrl = new AbortController();
      sendRef.current = ctrl;

      // Resolve context. Watchlist comes from localStorage instantly; bias pairs
      // we ask the server for, but we don't block on it forever.
      const watchlist = readWatchlist();
      const biasPairs = await fetchBiasPairs(ctrl.signal);

      try {
        const res = await api.post<ChatResponse>(
          '/api/ai/chat',
          {
            messages: toApiMessages(nextMessages),
            context: {
              watchlist: watchlist.length ? watchlist : undefined,
              biasPairs: biasPairs.length ? biasPairs : undefined,
            },
          },
          { signal: ctrl.signal },
        );

        const assistant: ChatMessage = {
          role: 'assistant',
          content: res?.message?.content ?? '',
        };
        setMessages((prev) => [...prev, assistant]);
        if (typeof res?.quotaRemaining === 'number') {
          setQuotaRemaining(res.quotaRemaining);
        }
      } catch (err) {
        // Aborted = user closed/cleared mid-flight; do nothing extra.
        if ((err as any)?.name === 'AbortError') return;

        if (err instanceof APIError) {
          const body = err.body || {};
          const remaining =
            typeof body.quotaRemaining === 'number' ? body.quotaRemaining : undefined;
          if (remaining != null) setQuotaRemaining(remaining);

          if (err.status === 429) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'system',
                content:
                  body.error ||
                  "You've hit today's AI chat limit. Try again tomorrow.",
              },
            ]);
          } else if (err.status === 503) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'system',
                content:
                  body.error ||
                  'AI is having a moment — please retry in a few seconds.',
                retry: true,
              },
            ]);
          } else if (err.status === 400) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'system',
                content: body.error || "That message couldn't be sent. Please try rephrasing.",
              },
            ]);
          } else {
            setMessages((prev) => [
              ...prev,
              {
                role: 'system',
                content: err.message || 'Something went wrong — please try again.',
              },
            ]);
          }
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: "Couldn't reach AI chat — check your connection and retry.",
              retry: true,
            },
          ]);
        }
      } finally {
        if (sendRef.current === ctrl) sendRef.current = null;
        setSending(false);
      }
    },
    [messages, sending],
  );

  // Retry resends the last user message (everything after it is replaced).
  const retryLast = useCallback(() => {
    if (sending) return;
    // Strip trailing system notes, then resend whatever the last user msg was.
    let lastUserIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx === -1) return;
    const lastUser = messages[lastUserIdx];
    // Keep history up to (but not including) the last user — sendMessage will
    // re-append it. Drop any trailing system notes too.
    const truncated = messages.slice(0, lastUserIdx);
    // Pass the truncated history explicitly so we don't race React batching.
    void sendMessage(lastUser.content, truncated);
  }, [messages, sending, sendMessage]);

  const clearHistory = useCallback(() => {
    if (sendRef.current) sendRef.current.abort();
    setMessages([]);
    setQuotaRemaining(undefined);
    setInput('');
  }, []);

  const handleExampleClick = useCallback((prompt: string) => {
    setInput(prompt);
  }, []);

  // Portal BOTH the bubble and the drawer to <body> — otherwise an ancestor
  // with `transform` (framer-motion uses transform extensively in AppShell)
  // becomes the containing block for `position: fixed`, and the bubble can
  // end up off-screen or hidden behind other UI.
  return createPortal(
    <>
      <ChatBubble open={open} onOpen={() => setOpen(true)} />
      <AnimatePresence>
        {open && (
          <ChatDrawer
            key="ai-chat-drawer"
            messages={messages}
            quotaRemaining={quotaRemaining}
            input={input}
            setInput={setInput}
            sending={sending}
            onClose={() => setOpen(false)}
            onSend={sendMessage}
            onClear={clearHistory}
            onRetry={retryLast}
            onExampleClick={handleExampleClick}
          />
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
}

/* ------------------------------- Bubble ---------------------------------- */

function ChatBubble({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  // Two-piece launcher: a label "bubble" sitting next to the round chat-icon
  // button. Both click to open. Hidden when the drawer is open.
  return (
    <div
      className={cn(
        'fixed bottom-8 right-5 z-40 inline-flex items-center gap-2',
        'transition-all duration-200 ease-out-expo',
        open && 'opacity-0 pointer-events-none scale-90',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open Trading Assistant"
        className={cn(
          'inline-flex items-center h-9 px-3 rounded-full',
          'bg-bg-elevated border border-border text-fg text-xs font-semibold tracking-tight',
          'shadow-sm hover:bg-bg-card transition-colors whitespace-nowrap',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
        )}
      >
        Trading Assistant
      </button>
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open Trading Assistant"
        className={cn(
          'size-12 rounded-full bg-accent text-[rgb(20_15_8)] shadow-elevation',
          'inline-flex items-center justify-center',
          'transition-all duration-200 ease-out-expo',
          'hover:scale-105 hover:shadow-[0_18px_40px_-10px_rgb(var(--accent)/0.55)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
        )}
      >
        <MessageCircle className="size-5" />
      </button>
    </div>
  );
}

/* ------------------------------- Drawer ---------------------------------- */

interface ChatDrawerProps {
  messages: ChatMessage[];
  quotaRemaining: number | undefined;
  input: string;
  setInput: (v: string) => void;
  sending: boolean;
  onClose: () => void;
  onSend: (text: string) => void;
  onClear: () => void;
  onRetry: () => void;
  onExampleClick: (prompt: string) => void;
}

function ChatDrawer({
  messages,
  quotaRemaining,
  input,
  setInput,
  sending,
  onClose,
  onSend,
  onClear,
  onRetry,
  onExampleClick,
}: ChatDrawerProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-scroll to bottom whenever messages change or while sending.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Auto-grow the textarea (cap ~5 rows ≈ 120px).
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const next = Math.min(ta.scrollHeight, 120);
    ta.style.height = `${next}px`;
  }, [input]);

  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const lastIsSystemRetry = !!(lastMessage && lastMessage.role === 'system' && lastMessage.retry);

  const showQuota = typeof quotaRemaining === 'number' && quotaRemaining <= 10;
  const empty = messages.length === 0;

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending && input.trim()) onSend(input);
    }
  }

  return (
    <>
      <motion.div
        key="ai-chat-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        onClick={onClose}
        // Mobile: dim the small strip above the drawer so tapping there dismisses.
        // Desktop: subtle dim across the whole screen, kept light so the dashboard
        // stays visible as context.
        className="fixed inset-0 z-50 bg-bg/40 backdrop-blur-[2px] sm:bg-transparent sm:backdrop-blur-0"
        aria-hidden="true"
      />
      <motion.aside
        key="ai-chat-panel"
        role="dialog"
        aria-modal="true"
        aria-label="AI chat"
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={{ duration: 0.22, ease: EASE }}
        className={cn(
          'fixed z-50 flex flex-col',
          'bg-bg-card border border-border rounded-2xl shadow-elevation overflow-hidden',
          // Mobile: near-fullscreen sheet
          'inset-x-4 bottom-4 top-[12vh]',
          // Desktop: anchored bottom-right, ~380px wide
          'sm:inset-auto sm:bottom-8 sm:right-5 sm:top-auto sm:left-auto',
          'sm:w-[380px] sm:max-w-[calc(100vw-2.5rem)] sm:h-[min(640px,calc(100vh-2.5rem))]',
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-10 bg-bg-elevated/95 backdrop-blur-md border-b border-border/70 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="inline-flex items-center justify-center size-9 rounded-lg bg-accent/15 text-accent shrink-0">
                <MessageCircle className="size-4" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-fg leading-tight">Telos AI</h2>
                <p className="text-2xs text-fg-subtle mt-0.5 leading-snug">
                  {showQuota
                    ? `${quotaRemaining} message${quotaRemaining === 1 ? '' : 's'} left today`
                    : "Grounded in today's data and your watchlist"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={onClear}
                disabled={messages.length === 0}
                aria-label="Clear chat history"
                title="Clear chat history"
                className={cn(
                  'p-1.5 rounded-md text-fg-muted hover:text-fg hover:bg-bg-card transition-colors',
                  'disabled:opacity-40 disabled:pointer-events-none',
                )}
              >
                <RotateCcw className="size-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close AI chat"
                className="p-1.5 rounded-md text-fg-muted hover:text-fg hover:bg-bg-card transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Message list */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
        >
          {empty ? (
            <EmptyState onExampleClick={onExampleClick} />
          ) : (
            <>
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  message={m}
                  onRetry={
                    m.role === 'system' && m.retry && i === messages.length - 1
                      ? onRetry
                      : undefined
                  }
                />
              ))}
              {sending && (
                // Rendered as a left-aligned assistant bubble so it's
                // unambiguously the AI's pending reply (not the user's
                // message still processing).
                <div className="flex justify-start">
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl rounded-bl-md px-3 py-2',
                      'bg-bg-elevated border border-border text-fg-muted',
                      'inline-flex items-center gap-1.5 text-xs',
                    )}
                  >
                    <span className="font-semibold text-fg">Telos AI</span>
                    <span className="inline-flex gap-0.5 ml-1" aria-label="thinking">
                      <span className="size-1.5 rounded-full bg-fg-subtle animate-pulse" />
                      <span className="size-1.5 rounded-full bg-fg-subtle animate-pulse [animation-delay:150ms]" />
                      <span className="size-1.5 rounded-full bg-fg-subtle animate-pulse [animation-delay:300ms]" />
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Input row */}
        <div className="border-t border-border/70 px-3 py-3 bg-bg-elevated/60">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              disabled={sending}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything…"
              className={cn(
                'flex-1 resize-none rounded-lg px-3 py-2 text-sm leading-relaxed',
                'bg-bg-card border border-border text-fg placeholder:text-fg-subtle',
                'transition-colors duration-150',
                'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'max-h-[120px]',
              )}
            />
            <button
              type="button"
              onClick={() => onSend(input)}
              disabled={sending || !input.trim()}
              aria-label="Send"
              className={cn(
                'inline-flex items-center justify-center shrink-0 size-9 rounded-lg',
                'bg-accent text-[rgb(20_15_8)] font-semibold',
                'transition-all duration-150 ease-out-expo',
                'hover:bg-accent/90 active:scale-[0.96]',
                'disabled:opacity-40 disabled:pointer-events-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
              )}
            >
              {sending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
            </button>
          </div>
          {lastIsSystemRetry && !sending && (
            <p className="mt-1.5 text-2xs text-fg-subtle">
              Tip: press Retry above to resend your last message.
            </p>
          )}
        </div>
      </motion.aside>
    </>
  );
}

/* --------------------------- Empty state -------------------------------- */

function EmptyState({ onExampleClick }: { onExampleClick: (prompt: string) => void }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-2 py-6">
      <span className="inline-flex items-center justify-center size-12 rounded-full bg-accent/15 text-accent">
        <MessageCircle className="size-5" />
      </span>
      <p className="mt-3 text-sm font-semibold text-fg">Ask anything about the markets</p>
      <p className="mt-1 text-2xs text-fg-subtle max-w-[260px]">
        Grounded in today's data and your watchlist. Try one of these to get started:
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
        {EXAMPLE_PROMPTS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onExampleClick(p)}
            className={cn(
              'rounded-full border border-border bg-bg-elevated px-3 py-1.5',
              'text-2xs font-semibold text-fg-muted hover:text-fg hover:border-border-strong',
              'transition-colors',
            )}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- Message bubble ------------------------------ */

function MessageBubble({
  message,
  onRetry,
}: {
  message: ChatMessage;
  onRetry?: () => void;
}) {
  if (message.role === 'system') {
    return (
      <div className="text-2xs text-fg-subtle italic leading-relaxed px-1 py-1 flex items-start gap-2">
        <span className="flex-1 whitespace-pre-wrap">{message.content}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={cn(
              'shrink-0 not-italic rounded-md px-2 py-0.5 text-2xs font-semibold',
              'bg-accent/15 text-accent hover:bg-accent/25 transition-colors',
            )}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const isUser = message.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
          isUser
            ? 'bg-accent text-bg rounded-br-md'
            : 'bg-bg-elevated border border-border text-fg rounded-bl-md',
        )}
      >
        {message.content}
      </div>
    </div>
  );
}
